import { useTranslation } from 'react-i18next';

const steps = [
  {
    number: '01',
    icon: 'ri-chat-smile-2-line',
    color: 'from-amber-400/20 to-amber-400/5',
    borderColor: 'border-amber-400/30',
    iconColor: 'text-amber-400',
    headline: 'You tell us what hurts',
    simple: 'Just like telling a friend — "my tooth hurts" or "I want a new smile." That\'s it. No forms, no confusion.',
    tag: 'Step 1 — Share Your Needs',
  },
  {
    number: '02',
    icon: 'ri-stethoscope-line',
    color: 'from-amber-300/15 to-amber-300/5',
    borderColor: 'border-amber-300/25',
    iconColor: 'text-amber-300',
    headline: 'We find your perfect doctor',
    simple: 'We match you with a real, certified specialist in Turkey — someone who has done this exact treatment hundreds of times.',
    tag: 'Step 2 — Meet Your Expert',
  },
  {
    number: '03',
    icon: 'ri-hospital-line',
    color: 'from-amber-500/15 to-amber-500/5',
    borderColor: 'border-amber-500/25',
    iconColor: 'text-amber-500',
    headline: 'You fly in, we handle everything',
    simple: 'Airport pickup, hotel, clinic — all sorted. You just show up. We take care of the rest like a personal assistant.',
    tag: 'Step 3 — Arrive & Relax',
  },
  {
    number: '04',
    icon: 'ri-shield-check-line',
    color: 'from-amber-400/20 to-amber-400/5',
    borderColor: 'border-amber-400/30',
    iconColor: 'text-amber-400',
    headline: 'We stay with you — even after',
    simple: 'Back home? We\'re still here. Your doctor checks in, answers questions, and makes sure everything healed perfectly.',
    tag: 'Step 4 — Ongoing Care',
  },
];

export default function HowItWorks() {
  const { t } = useTranslation();

  return (
    <section id="how-it-works" className="py-24 md:py-36 bg-[#0d0d0d] relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-400/3 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">

        {/* Header */}
        <div className="text-center mb-16 md:mb-24">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-10 h-px bg-amber-400/40" />
            <span
              className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              How It Works
            </span>
            <div className="w-10 h-px bg-amber-400/40" />
          </div>
          <h2
            className="text-4xl md:text-6xl font-normal text-white leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            {t('how.title') || 'Simple as 1, 2, 3, 4.'}
          </h2>
          <p
            className="text-white/40 text-lg max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            No medical jargon. No confusing steps. Here's exactly what happens from your first message to your final check-up.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative bg-gradient-to-br ${step.color} border ${step.borderColor} rounded-2xl p-8 md:p-10 group hover:border-amber-400/50 transition-all duration-500`}
            >
              {/* Step tag */}
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="text-xs text-white/30 tracking-widest uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.tag}
                </span>
              </div>

              {/* Icon + Number row */}
              <div className="flex items-start justify-between mb-6">
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 ${step.iconColor}`}>
                  <i className={`${step.icon} text-2xl`}></i>
                </div>
                <span
                  className="text-7xl md:text-8xl font-normal text-white/5 group-hover:text-white/8 transition-colors duration-300 leading-none"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.number}
                </span>
              </div>

              {/* Headline */}
              <h3
                className="text-2xl md:text-3xl font-normal text-white mb-4 leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {step.headline}
              </h3>

              {/* Simple explanation */}
              <p
                className="text-white/55 text-base leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {step.simple}
              </p>

              {/* Connector arrow (not on last) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-20">
                  {index % 2 === 0 ? null : null}
                </div>
              )}

              {/* Bottom accent line */}
              <div className={`mt-8 w-8 h-0.5 bg-amber-400/40 group-hover:w-16 group-hover:bg-amber-400/70 transition-all duration-500`} />
            </div>
          ))}
        </div>

        {/* Flow connector visual */}
        <div className="hidden md:flex items-center justify-center gap-0 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className="flex flex-col items-center gap-2">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-amber-400/10 border border-amber-400/30 ${step.iconColor}`}>
                  <i className={`${step.icon} text-base`}></i>
                </div>
                <span
                  className="text-white/30 text-xs tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.number}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className="flex items-center mx-3">
                  <div className="w-16 h-px bg-amber-400/20" />
                  <i className="ri-arrow-right-line text-amber-400/30 text-xs -ml-1"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom summary bar */}
        <div className="border border-white/8 rounded-2xl p-8 md:p-10 bg-white/2 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex flex-col gap-2">
            <p
              className="text-white text-xl md:text-2xl font-normal leading-snug"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              That's the whole thing. No surprises.
            </p>
            <p
              className="text-white/35 text-sm leading-relaxed max-w-lg"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              From your first message to your last follow-up, we're with you every single step. You never have to figure anything out alone.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/book-treatment')}
              className="flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('how.cta') || 'Start My Journey'}
              <i className="ri-arrow-right-line"></i>
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/about')}
              className="flex items-center gap-3 border border-white/15 hover:border-amber-400/40 text-white/60 hover:text-amber-400 px-8 py-4 font-medium text-sm tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Learn More
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
